import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle,
  Info,
  Shield,
  MapPin,
  Mail,
  Link2,
  ChevronDown,
  ChevronUp
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DisclaimerSection = ({ title, icon: Icon, items, type = "warning" }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className="border-b border-border last:border-0 py-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${type === "info" ? "text-primary" : "text-amber-500"}`} />
          <span className="text-sm font-medium">{title}</span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {expanded && (
        <ul className="mt-2 space-y-1 pl-6">
          {items.map((item, i) => (
            <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
              <span className="text-muted-foreground">•</span>
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const SystemDisclaimers = () => {
  const { getAuthHeader } = useAuth();
  const [capabilities, setCapabilities] = useState(null);

  useEffect(() => {
    const fetchCapabilities = async () => {
      try {
        const response = await axios.get(`${API}/system/capabilities`, {
          headers: getAuthHeader()
        });
        setCapabilities(response.data);
      } catch (error) {
        console.error("Failed to fetch capabilities");
      }
    };
    fetchCapabilities();
  }, []);

  if (!capabilities) return null;

  const vpnService = capabilities.vpn_detection?.service;
  const vpnAccuracy = capabilities.vpn_detection?.accuracy;

  return (
    <Card className="bg-card/50 border-border">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Info className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">Detection Accuracy</span>
          </div>
          <Badge 
            variant="outline" 
            className={vpnService === "ipqs" ? "border-emerald-500/50 text-emerald-500" : "border-amber-500/50 text-amber-500"}
          >
            {vpnService === "ipqs" ? "Enhanced" : "Basic"}
          </Badge>
        </div>
        
        <div className="space-y-0">
          <DisclaimerSection
            title={`VPN/Proxy Detection (${vpnAccuracy})`}
            icon={Shield}
            type={vpnService === "ipqs" ? "info" : "warning"}
            items={
              vpnService === "ipqs" 
                ? [
                    "Using IPQualityScore for ~95% detection accuracy",
                    "Free tier: 1,000 checks/month, 35/day",
                    "Tor exit nodes and bots also detected"
                  ]
                : [
                    "Basic keyword matching only (~30% accuracy)",
                    "Many VPNs will NOT be detected",
                    "Add IPQS API key for better detection"
                  ]
            }
          />
          
          <DisclaimerSection
            title="Geolocation Accuracy"
            icon={MapPin}
            items={capabilities.geolocation?.limitations || []}
          />
          
          <DisclaimerSection
            title="Email Tracking"
            icon={Mail}
            items={capabilities.email_tracking?.limitations || []}
          />
          
          <DisclaimerSection
            title="Link Tracking"
            icon={Link2}
            items={capabilities.link_tracking?.limitations || []}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default SystemDisclaimers;
